import moment from "moment";

import { RPM_PATIENT_STATUS, RPM_PORTAL_ACCESS_STATUS } from "../../../constants";
import Strings from "../../../Strings";
import { ACTIVATION_METHODS, getProgramsFromPatient, isAccessAlignmentPending, isAccessEligibilityPending, isAccessProgram, isAccessRegistrationPending } from "../../../utils/cmsPrograms";
import { hasPermission, ORG_FEATURES, orgFeatureEnabled, PERMISSIONS } from "../../../utils/userPermissions";

export const getPatientEnrollmentEntries = patient => Object.entries(patient?.patientEnrollment?.enrollments || {});

const getEnrollmentActionContext = data => {
  if (data?.patient && data?.enrollment && data?.enrollmentId) {
    return data;
  }
  const [enrollmentId, enrollment] = getPatientEnrollmentEntries(data)[0] || [];
  return { patient: data, enrollment, enrollmentId, patientView: false };
};

const isEnrollmentEndDateSet = (patient, enrollmentId) => {
  const enrollments = patient?.patientEnrollment?.enrollments;
  return enrollmentId
    ? !!enrollments?.[enrollmentId]?.enrollmentEnd
    : Object.values(enrollments || {}).some(enrollment => enrollment.enrollmentEnd);
};

export const getEnrollmentReadyToStart = (patient, enrollmentId) => {
  const programs = getProgramsFromPatient(patient, [RPM_PATIENT_STATUS.enrolled]);
  if (programs?.length === 0) {
    return false;
  }
  return programs.find(program => (
    (!enrollmentId || program.enrollmentId === enrollmentId) &&
    program.kitAccepted &&
    (program.enrollmentStart || program.activationMethod !== ACTIVATION_METHODS.Manual)
  ));
};

const patientHasEnrollmentStatus = (patient, statuses) => getPatientEnrollmentEntries(patient)
  .some(([, enrollment]) => statuses.includes(enrollment.status));

export const createEnrollmentButtons = ({
  extendedOutreachStatusEnabled,
  endEnrollment,
  enrollPatient,
  handleStartEnrollment,
  registerPatient,
  resetPatientStatus,
  reRegisterPatient,
  rescheduleTelevisit,
  unregisterEnrollment,
  changeOutreachStatus,
  cancelEnrollment,
  addMedicine,
  checkEligibility,
  moveToNotEnrolled,
  editEnrollment,
}) => {
  const buttonsArray = [];

  const getPrimaryEnrollmentActionText = context => {
    if (isAccessRegistrationPending(context.enrollment)) {
      return Strings.accessEligibilityAwaitingConfirmation;
    }
    if (isAccessAlignmentPending(context.enrollment)) {
      return Strings.accessAlignmentAwaitingConfirmation;
    }
      return context.enrollment?.status === RPM_PATIENT_STATUS.activated && isEnrollmentEndDateSet(context.patient, context.enrollmentId)
    ? Strings.changeEnrollmentEndDate
    : context.enrollment?.status === RPM_PATIENT_STATUS.activated
    ? Strings.disenroll
    : context.enrollment?.status === RPM_PATIENT_STATUS.enrolled && getEnrollmentReadyToStart(context.patient, context.enrollmentId)
    ? Strings.activateNow
    : context.enrollment?.status === RPM_PATIENT_STATUS.enrolled
    ? Strings.assignKit
    : context.enrollment?.status === RPM_PATIENT_STATUS.verified
    ? Strings.enroll
    : context.enrollment?.status === RPM_PATIENT_STATUS.notEnrolled
    ? Strings.moveToOnboarded
    : context.enrollment?.status === RPM_PATIENT_STATUS.ended
    ? Strings.initiateNewEnrollment
    : extendedOutreachStatusEnabled
    ? Strings.changeStatus
    : Strings.register;
  };

  const isAccessPendingForEnrollment = enrollment =>
    isAccessRegistrationPending(enrollment) || isAccessAlignmentPending(enrollment);

  const getAccessPendingTooltip = enrollment => (
    isAccessRegistrationPending(enrollment)
      ? Strings.accessEligibilityAwaitingConfirmation
      : isAccessAlignmentPending(enrollment)
      ? Strings.accessAlignmentAwaitingConfirmation
      : ''
  );

  // End enrollment (for activated enrollments)
  buttonsArray.push({
    visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.activated,
    icon: 'endDate',
    onClick: (_, data) => endEnrollment(getEnrollmentActionContext(data)),
    text: (_, data) => getPrimaryEnrollmentActionText(getEnrollmentActionContext(data)),
    disabled: (_, data) => isAccessPendingForEnrollment(getEnrollmentActionContext(data).enrollment),
    disabledTooltip: (_, data) => getAccessPendingTooltip(getEnrollmentActionContext(data).enrollment),
  });

  // Enroll patient (for verified enrollments)
  buttonsArray.push({
    visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.verified,
    icon: 'enroll',
    onClick: (_, data) => enrollPatient(getEnrollmentActionContext(data)),
    text: (_, data) => getPrimaryEnrollmentActionText(getEnrollmentActionContext(data)),
    highlighted: true,
    disabled: (_, data) => {
      const { enrollment } = getEnrollmentActionContext(data);
      return isAccessPendingForEnrollment(enrollment)
        || isAccessEligibilityPending(enrollment)
        || !hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_ENROLL);
    },
    disabledTooltip: (_, data) => {
      const { enrollment } = getEnrollmentActionContext(data);
      return getAccessPendingTooltip(enrollment)
        || (isAccessEligibilityPending(enrollment) ? Strings.accessEligibilityAwaitingConfirmation : '')
        || Strings.capPatient.youDontHaveRightsToEnroll;
    },
  });

  // Activate enrollment (for enrolled patients)
  buttonsArray.push({
    visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.enrolled,
    icon: 'activate',
    onClick: (_, data) => {
      const context = getEnrollmentActionContext(data);
      if (getEnrollmentReadyToStart(context.patient, context.enrollmentId)) {
        handleStartEnrollment(context);
      } else {
        enrollPatient(context);
      }
    },
    text: (_, data) => getPrimaryEnrollmentActionText(getEnrollmentActionContext(data)),
    highlighted: true,
    disabled: (_, data) => {
      const { enrollment } = getEnrollmentActionContext(data);
      return isAccessPendingForEnrollment(enrollment)
        || !hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_PROGRAM_ACTIVATION)
        || (enrollment.shipment && enrollment.shipment.status !== 'delivered');
    },
    disabledTooltip: (_, data) => {
      const { enrollment } = getEnrollmentActionContext(data);
      return getAccessPendingTooltip(enrollment)
        || (enrollment.shipment && enrollment.shipment.status !== 'delivered'
          ? Strings.capPatient.cantAssignKitToPatientWithShipment
          : '')
        || Strings.capPatient.youDontHaveRightsToActivate;
    },
  });

  // Register patient (for onboarded enrollments)
  buttonsArray.push({
    visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.onboarded,
    icon: 'register',
    onClick: (_, data) => registerPatient(getEnrollmentActionContext(data)),
    text: (_, data) => getPrimaryEnrollmentActionText(getEnrollmentActionContext(data)),
    highlighted: true,
    disabled: (_, data) => isAccessPendingForEnrollment(getEnrollmentActionContext(data).enrollment),
    disabledTooltip: (_, data) => getAccessPendingTooltip(getEnrollmentActionContext(data).enrollment),
  });

  // Reset patient status (for ended enrollments)
  buttonsArray.push({
    visible: (_, data) => {
      const context = getEnrollmentActionContext(data);
      return context.enrollment?.status === RPM_PATIENT_STATUS.ended;
    },
    icon: 'register',
    testId: 'reset-enrollment',
    onClick: (_, data) => resetPatientStatus(getEnrollmentActionContext(data).patient),
    text: (_, data) => getPrimaryEnrollmentActionText(getEnrollmentActionContext(data)),
    disabled: (_, data) => isAccessPendingForEnrollment(getEnrollmentActionContext(data).enrollment),
    disabledTooltip: (_, data) => getAccessPendingTooltip(getEnrollmentActionContext(data).enrollment),
  });

  // Register patient (for not enrolled enrollments)
  buttonsArray.push({
    visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.notEnrolled,
    icon: 'register',
    onClick: (_, data) => {
      const context = getEnrollmentActionContext(data);
      if (isAccessProgram(context.enrollment?.program)) {
        registerPatient(context);
      } else {
        reRegisterPatient(context);
      }
    },
    text: (_, data) => getPrimaryEnrollmentActionText(getEnrollmentActionContext(data)),
    disabled: (_, data) => isAccessPendingForEnrollment(getEnrollmentActionContext(data).enrollment),
    disabledTooltip: (_, data) => getAccessPendingTooltip(getEnrollmentActionContext(data).enrollment),
  });

  // Schedule / reschedule televisit (for verified enrollments)
  buttonsArray.push({
    visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.verified,
    icon: 'endDate',
    onClick: (_, data) => rescheduleTelevisit(getEnrollmentActionContext(data)),
    text: (_, data) => getEnrollmentActionContext(data).patient?.enrollmentSchedules?.length > 0
      ? Strings.capPatient.rescheduleTelevisit
      : Strings.capPatient.scheduleTelevisit,
  });

  // Unregister enrollment (for verified enrollments)
  buttonsArray.push({
    visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.verified,
    icon: 'unregister',
    onClick: (_, data) => unregisterEnrollment(getEnrollmentActionContext(data)),
    text: Strings.unregister,
  });

  // Change outreach status (for verified enrollments)
  if (extendedOutreachStatusEnabled) {
    buttonsArray.push({
      visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.verified,
      icon: 'unregister',
      onClick: (_, data) => changeOutreachStatus(getEnrollmentActionContext(data)),
      text: Strings.changeStatus,
    });
  }
  
  // Cancel enrollment (for enrolled enrollments)
  buttonsArray.push({
    visible: (_, data) => getEnrollmentActionContext(data).enrollment?.status === RPM_PATIENT_STATUS.enrolled,
    icon: 'cancelEnrollment',
    onClick: (_, data) => cancelEnrollment(getEnrollmentActionContext(data)),
    text: Strings.cancelEnrollment,
  });

  // Add schedule (for enrolled and activated enrollments, so it can be added before activation)
  buttonsArray.push({
    visible: (_, data) => [RPM_PATIENT_STATUS.enrolled, RPM_PATIENT_STATUS.activated]
      .includes(getEnrollmentActionContext(data).enrollment?.status),
    icon: 'addMedication',
    onClick: (_, data) => addMedicine(getEnrollmentActionContext(data)),
    text: Strings.patient_medications.addScheduleTitle,
  });

  // Check eligibility (for activated enrollments)
  if (orgFeatureEnabled(ORG_FEATURES.inovalon_enabled)) {
    buttonsArray.push({
      visible: (_, data) => {
        const context = getEnrollmentActionContext(data);
        return !context.patientView && !isAccessProgram(context.enrollment?.program);
      },
      icon: 'check',
      onClick: (_, data) => checkEligibility(getEnrollmentActionContext(data).patient),
      text: Strings.checkEligibility,
    });
  }

/*     if (showShippingDetails) {
    buttonsArray.push({
      visible: (_, data) => {
        const { enrollment } = getEnrollmentActionContext(data);
        return enrollment?.status === RPM_PATIENT_STATUS.ended &&
          !!(enrollment?.kitUnguessableId || enrollment?.shipment?.kit_id);
      },
      icon: 'package',
      onClick: (_, data) => {
        const { patient, enrollment } = getEnrollmentActionContext(data);
        onNavigate('/shipping/order-details/new', {
          order: {
            patient_uuid: patient.masked_id,
            order_type: "standard",
            flow: "return",
            source_kit_uuid: enrollment.kitUnguessableId || enrollment.shipment?.kit_id,
            origin_address: patient.address,
          },
        });
      },
      text: Strings.shipping.createReturnShipmentOrder,
    });
  } */

  // move to not enrolled
  if (!extendedOutreachStatusEnabled) {
    buttonsArray.push({
      visible: (_, data) => [RPM_PATIENT_STATUS.onboarded, RPM_PATIENT_STATUS.verified]
        .includes(getEnrollmentActionContext(data).enrollment?.status),
      icon: 'notEnrolled',
      onClick: (_, data) => moveToNotEnrolled(getEnrollmentActionContext(data)),
      text: Strings.moveToNotEnrolled,
    });
  }

  // edit enrollment
  buttonsArray.push({
    visible: (_, data) => {
      const { enrollment } = getEnrollmentActionContext(data);
      const canEnrollmentBeEdited = 
        (enrollment?.enrollmentEnd && moment().isBefore(enrollment.enrollmentEnd) || !enrollment?.enrollmentEnd)
        && enrollment?.program === 'RPM';
      return canEnrollmentBeEdited && [RPM_PATIENT_STATUS.enrolled, RPM_PATIENT_STATUS.activated]
        .includes(enrollment?.status);
    },
    icon: 'edit',
    testId: 'edit-enrollment',
    onClick: (_, data) => {
      const context = getEnrollmentActionContext(data);
      editEnrollment(context);
    },
    text: Strings.editEnrollment,
  });

  return buttonsArray;
};

export const createPatientButtons = ({
  cohortId,
  sendSMS,
  checkEligibility,
  editPatient,
  unassignPatient,
  deletePatient,
  resetPassword,
  inviteToPortal,
}) => {
    const buttonsArray = [];

    if (orgFeatureEnabled(ORG_FEATURES.patient_portal_access)) {
      buttonsArray.push({
        visible: (_, patient) => patientHasEnrollmentStatus(patient, [RPM_PATIENT_STATUS.activated]) &&
          Object.values(RPM_PORTAL_ACCESS_STATUS).includes(patient?.portalAccess),
        icon: (_, patient) => patient?.portalAccess === RPM_PORTAL_ACCESS_STATUS.hasAccess ? 'refresh' : 'invite',
        onClick: (_, patient) => patient?.portalAccess === RPM_PORTAL_ACCESS_STATUS.hasAccess
          ? resetPassword(patient)
          : inviteToPortal(patient),
        text: (_, patient) => patient?.portalAccess === RPM_PORTAL_ACCESS_STATUS.noAccess
          ? Strings.inviteToPortal
          : patient?.portalAccess === RPM_PORTAL_ACCESS_STATUS.invited
          ? Strings.reInviteToPortal
          : Strings.resetPassword,
        disabled: (_, patient) => !patient?.email,
        disabledTooltip: Strings.emailNeededForPortalAccess,
      });
    }

    buttonsArray.push({
      visible: (_, patient) => patientHasEnrollmentStatus(patient, [RPM_PATIENT_STATUS.activated]),
      icon: 'message',
      onClick: (_, patient) => sendSMS(patient),
      text: Strings.sendText,
      disabled: (_, patient) => !patient?.manualSmsEnabled,
      disabledTooltip: Strings.messages.manualSmsDisabled,
    });

    if (orgFeatureEnabled(ORG_FEATURES.inovalon_enabled)) {
      buttonsArray.push({
        visible: (_, patient) => !getPatientEnrollmentEntries(patient)
          .some(([, enrollment]) => isAccessProgram(enrollment.program)),
        icon: 'check',
        onClick: (_, patient) => checkEligibility(patient),
        text: Strings.checkEligibility,
      });
    }

    buttonsArray.push({
      visible: (_, patient) => getPatientEnrollmentEntries(patient).length === 0 ||
        getPatientEnrollmentEntries(patient).some(([, enrollment]) =>
          ![RPM_PATIENT_STATUS.ended, RPM_PATIENT_STATUS.notEnrolled].includes(enrollment.status)),
    icon: 'edit',
    testId: 'edit-patient',
      onClick: (_, patient) => editPatient(patient),
      text: Strings.editPatientInfo,
    });

    buttonsArray.push({
      visible: (_, patient) => !patientHasEnrollmentStatus(
        patient,
        [RPM_PATIENT_STATUS.activated, RPM_PATIENT_STATUS.enrolled],
      ),
      icon: 'delete',
      onClick: cohortId ? (_, patient) => unassignPatient(patient) : (_, patient) => deletePatient(patient),
      text: cohortId ? Strings.capPatient.unassignFromCohort : Strings.deletePatient,
    });

    return buttonsArray;
  };
